/**
 * Area Role - Constants
 *
 * Fatores de conversão, símbolos e aliases para unidades de área.
 *
 * Fontes:
 * - NIST SP 811 (2008)
 * - International Yard and Pound Agreement (1959)
 * - SI Brochure, 9th Edition (2019)
 */

import type { SimpleUnitConfig, UnitAliases } from "../../contracts";

// =============================================================================
// TYPES
// =============================================================================

export type AreaUnit =
  // SI
  | "square_kilometer"
  | "hectare"
  | "are"
  | "square_meter"
  | "square_decimeter"
  | "square_centimeter"
  | "square_millimeter"
  // Imperial/US
  | "square_mile"
  | "acre"
  | "square_yard"
  | "square_foot"
  | "square_inch";

// =============================================================================
// UNITS CONFIG
// =============================================================================

/**
 * Configuração de cada unidade de área.
 *
 * TODOS os fatores são EXATOS:
 * - SI: derivados de potências de 10
 * - Imperial: derivados de (comprimento)² onde comprimento é exato desde 1959
 *
 * Relações imperiais:
 * - 1 in² = (0.0254 m)² = 0.00064516 m²
 * - 1 ft² = (0.3048 m)² = 0.09290304 m²
 * - 1 yd² = (0.9144 m)² = 0.83612736 m²
 * - 1 mi² = (1609.344 m)² = 2589988.110336 m²
 * - 1 acre = 43560 ft² = 4046.8564224 m²
 */
export const AREA_UNITS: Record<AreaUnit, SimpleUnitConfig> = {
  // SI Units (todos exatos)
  square_kilometer: {
    factor: 1_000_000,
    symbol: "km²",
    singular: "square kilometer",
    plural: "square kilometers",
  },
  hectare: {
    factor: 10_000,
    symbol: "ha",
    singular: "hectare",
    plural: "hectares",
  },
  are: {
    factor: 100,
    symbol: "a",
    singular: "are",
    plural: "ares",
  },
  square_meter: {
    factor: 1,
    symbol: "m²",
    singular: "square meter",
    plural: "square meters",
  },
  square_decimeter: {
    factor: 0.01,
    symbol: "dm²",
    singular: "square decimeter",
    plural: "square decimeters",
  },
  square_centimeter: {
    factor: 0.0001,
    symbol: "cm²",
    singular: "square centimeter",
    plural: "square centimeters",
  },
  square_millimeter: {
    factor: 0.000001,
    symbol: "mm²",
    singular: "square millimeter",
    plural: "square millimeters",
  },

  // Imperial/US (todos exatos, derivados de comprimento² desde 1959)
  square_mile: {
    factor: 2_589_988.110336,
    symbol: "mi²",
    singular: "square mile",
    plural: "square miles",
  },
  acre: {
    factor: 4_046.8564224,
    symbol: "ac",
    singular: "acre",
    plural: "acres",
  },
  square_yard: {
    factor: 0.83612736,
    symbol: "yd²",
    singular: "square yard",
    plural: "square yards",
  },
  square_foot: {
    factor: 0.09290304,
    symbol: "ft²",
    singular: "square foot",
    plural: "square feet",
  },
  square_inch: {
    factor: 0.00064516,
    symbol: "in²",
    singular: "square inch",
    plural: "square inches",
  },
};

// =============================================================================
// ALIASES
// =============================================================================

/**
 * Aliases para reconhecimento de unidades no cast
 */
export const AREA_ALIASES: UnitAliases = {
  // square_kilometer
  "km²": "square_kilometer",
  km2: "square_kilometer",
  // hectare
  ha: "hectare",
  // are
  a: "are",
  // square_meter
  "m²": "square_meter",
  m2: "square_meter",
  // square_decimeter
  "dm²": "square_decimeter",
  dm2: "square_decimeter",
  // square_centimeter
  "cm²": "square_centimeter",
  cm2: "square_centimeter",
  // square_millimeter
  "mm²": "square_millimeter",
  mm2: "square_millimeter",
  // square_mile
  "mi²": "square_mile",
  mi2: "square_mile",
  // acre
  ac: "acre",
  // square_yard
  "yd²": "square_yard",
  yd2: "square_yard",
  // square_foot
  "ft²": "square_foot",
  ft2: "square_foot",
  // square_inch
  "in²": "square_inch",
  in2: "square_inch",
};

// =============================================================================
// HELPERS
// =============================================================================

/** Lista de todas as unidades */
export const AREA_UNIT_LIST = Object.keys(AREA_UNITS) as AreaUnit[];

/** Fatores de conversão (para compatibilidade) */
export const AREA_FACTORS: Record<AreaUnit, number> = Object.fromEntries(
  Object.entries(AREA_UNITS).map(([unit, config]) => [
    unit,
    config.factor ?? 1,
  ]),
) as Record<AreaUnit, number>;
